import React, { useMemo } from 'react';
import { ResponsiveContainer, Sankey, Tooltip as ReTooltip, Layer, Rectangle } from 'recharts';

/**
 * Sankey diagram renderer for the custom-viz registry.
 *
 * Data shape expected from INZARA query engine:
 *   - rowDims = ['source_field', 'target_field']
 *   - vKeys   = ['flow_value']
 *   - data    = [{ source_field, target_field, flow_value }, ...]
 *
 * Maps to recharts Sankey shape:
 *   { nodes: [{name}], links: [{source: idx, target: idx, value}] }
 */
function CustomNode({ x, y, width, height, index, payload, colors, onDrill }) {
  const isOut = payload.sourceLinks?.length;
  const fill = colors[index % colors.length];
  return (
    <Layer key={`node-${index}`}>
      <Rectangle
        x={x} y={y} width={width} height={height}
        fill={fill} fillOpacity={0.9}
        style={{ cursor: onDrill ? 'pointer' : 'default' }}
        onClick={() => onDrill && onDrill(payload.name)}
      />
      <text
        textAnchor={isOut ? 'end' : 'start'}
        x={isOut ? x - 6 : x + width + 6}
        y={y + height / 2}
        dy="0.3em"
        fontSize={10}
        fill="currentColor"
        style={{ pointerEvents: 'none' }}
      >
        {payload.name}
      </text>
    </Layer>
  );
}

export function SankeyRenderer({ data, rowDims, vKeys, config = {}, colors = [], onDrill, height = 340 }) {
  const sankeyData = useMemo(() => {
    if (!data || data.length === 0) return null;
    if (!rowDims || rowDims.length < 2 || !vKeys || !vKeys[0]) return null;
    const [srcKey, tgtKey] = rowDims;
    const valKey = vKeys[0];
    const namesSet = new Set();
    for (const r of data) {
      if (r[srcKey] != null) namesSet.add(String(r[srcKey]));
      if (r[tgtKey] != null) namesSet.add(String(r[tgtKey]));
    }
    const nodes = Array.from(namesSet).map(name => ({ name }));
    const idx = Object.fromEntries(nodes.map((n, i) => [n.name, i]));
    const links = [];
    for (const r of data) {
      const s = r[srcKey], t = r[tgtKey], v = Number(r[valKey] || 0);
      if (s == null || t == null || !Number.isFinite(v) || v <= 0) continue;
      if (idx[s] === idx[t]) continue; // skip self-loops
      links.push({ source: idx[s], target: idx[t], value: v });
    }
    return { nodes, links };
  }, [data, rowDims, vKeys]);

  if (!sankeyData) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-muted-foreground/80 italic px-4 text-center">
        Sankey requires 2 dimensions (source &amp; target) and 1 measure (flow value).
      </div>
    );
  }
  if (sankeyData.links.length === 0) {
    return <p className="text-xs text-muted-foreground py-8 text-center">No flows to display.</p>;
  }

  const palette = colors && colors.length ? colors : ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
  const linkColor = config.linkColor || palette[0];
  const nodePadding = Math.max(0, Math.min(80, Number(config.nodePadding ?? 20)));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <Sankey
        data={sankeyData}
        nodePadding={nodePadding}
        nodeWidth={Number(config.nodeWidth ?? 12)}
        link={{ stroke: linkColor, strokeOpacity: Number(config.linkOpacity ?? 0.4) }}
        node={(nodeProps) => <CustomNode {...nodeProps} colors={palette} onDrill={onDrill} />}
        margin={{ top: 10, right: 80, bottom: 10, left: 80 }}
      >
        <ReTooltip
          contentStyle={{
            background: 'rgba(20,20,30,0.92)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8, fontSize: 11,
          }}
          labelStyle={{ color: '#aaa' }}
          itemStyle={{ color: '#fff' }}
        />
      </Sankey>
    </ResponsiveContainer>
  );
}
